import Layout from 'components/ui/Layout';
import { useCustomers } from 'features/customer/hooks/useCustomers';
import { usePrefetchCustomers } from 'features/customer/hooks/usePrefetchCustomers';
import { useURLSync } from 'features/customer/hooks/useURLSync';
import { customerServices } from 'features/customer/services/customer.service';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Edit,
  Eye,
  Search,
  Trash2,
  UserPlus,
  X,
  ShieldCheck,
  User
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { formatDateTime } from 'utils/dateFormatter';
import { useNavigate } from 'react-router-dom';

const CUSTOMER_STATUSES = [
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'blocked', label: 'Blocked' },
  { key: 'deleted', label: 'Deleted' },
];

const KYC_STATUSES = [
  { key: 'pending', label: 'Pending' },
  { key: 'under_review', label: 'Under Review' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'expired', label: 'Expired' },
];

const CustomerManagement = () => {
  const navigate = useNavigate();

  // Filter state synced with URL
  const [filters, setFilters] = useURLSync({
    page: '1',
    limit: '10',
    status: '',
    kycStatus: '',
    phoneVerified: '',
    emailVerified: '',
    search: '',
    sortBy: 'createdAt',
    order: 'desc',
  });

  const [searchInput, setSearchInput] = useState(filters.search);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Fetch customers
  const { data, isPending, isError, error } = useCustomers(filters);
  const customers = data?.data?.customers;
  const pagination = data?.data?.pagination;

  // Prefetch
  const { prefetchNextPage, prefetchPrevPage } = usePrefetchCustomers();

  // Prefetch adjacent pages
  useEffect(() => {
    if (pagination) {
      const { page: currentPage, totalPages } = pagination;
      prefetchNextPage(filters, totalPages);
      if (currentPage > 1) {
        prefetchPrevPage(filters);
      }
    }
  }, [data, filters]);

  // Update filter and reset to page 1
  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : '1',
    }));
  };

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        updateFilter('search', searchInput);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  // Sort handler
  const handleSort = (column) => {
    const newOrder =
      filters.sortBy === column && filters.order === 'asc' ? 'desc' : 'asc';
    setFilters((prev) => ({
      ...prev,
      sortBy: column,
      order: newOrder,
    }));
  };

  // Export handler
const handleExport = async () => {
  try {
    const response =
      await customerServices.exportCustomers(
        {
          search:
            filters.search,

          status:
            filters.status,

          kycStatus:
            filters.kycStatus,

          phoneVerified:
            filters.phoneVerified,

          emailVerified:
            filters.emailVerified,

          sortBy:
            filters.sortBy,

          order:
            filters.order,
        }
      );

    const blob = new Blob(
      [response.data],
      {
        type: 'text/csv',
      }
    );

    const url =
      window.URL.createObjectURL(
        blob
      );

    const link =
      document.createElement('a');

    link.href = url;

    link.download = `customers-${Date.now()}.csv`;

    document.body.appendChild(
      link
    );

    link.click();

    document.body.removeChild(
      link
    );

    window.URL.revokeObjectURL(
      url
    );
  } catch (error) {
    console.error(
      'Failed to export customers',
      error
    );
  }
};

  const SortIcon = ({ column }) => {
    if (filters.sortBy !== column)
      return <span className='text-muted-foreground'>↕</span>;
    return filters.order === 'asc' ? <span>↑</span> : <span>↓</span>;
  };

  const handleEditClick = (customer) => {
    setIsEditModalOpen(true);
    setSelectedCustomer(customer);
  };

  const handleDeleteClick = (customer) => {
    setSelectedCustomer(customer);
    setIsDeleteModalOpen(true);
  };

  const handleViewClick = (customer) => {
    navigate(`/customers/${customer._id}`);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-500/10 text-green-600 dark:text-green-400',
      inactive: 'bg-gray-500/10 text-gray-600 dark:text-gray-400',
      suspended: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
      blocked: 'bg-red-500/10 text-red-600 dark:text-red-400',
      deleted: 'bg-red-500/10 text-red-600 dark:text-red-400',
    };
    return colors[status] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  };

  const getKycStatusColor = (status) => {
    const colors = {
      approved: 'bg-green-500/10 text-green-600 dark:text-green-400',
      pending: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
      under_review: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      rejected: 'bg-red-500/10 text-red-600 dark:text-red-400',
      expired: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
    };
    return colors[status] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  };

  const getVerificationBadge = (verified) => {
    return verified ? (
      <span className='px-2 py-1 text-xs font-medium rounded-full bg-green-500/10 text-green-600 dark:text-green-400'>
        Verified
      </span>
    ) : (
      <span className='px-2 py-1 text-xs font-medium rounded-full bg-gray-500/10 text-gray-600 dark:text-gray-400 whitespace-nowrap'>
        Not Verified
      </span>
    );
  };

  return (
    <Layout>
      <div className='min-h-screen bg-background text-foreground p-6'>
        <div className='max-w-7xl mx-auto'>
          {/* Header */}
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0'>
            <div>
              <h3 className='text-lg font-semibold text-foreground mb-1'>
                Customer Management
              </h3>
              <p className='text-sm text-muted-foreground'>
                Manage customers and account status
              </p>
            </div>

            <div className='flex space-x-3'>
              {/* <button
                onClick={() => setIsAddModalOpen(true)}
                className='flex items-center space-x-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors'
              >
                <UserPlus size={16} />
                <span>Add Customer</span>
              </button> */}

             {!isError && (
  <button
    onClick={handleExport}
    className='flex items-center space-x-2 px-4 py-2 text-sm border border-border rounded-md bg-card text-card-foreground hover:bg-accent transition-colors'
  >
    <Download size={16} />
    <span>Export</span>
  </button>
)}
            </div>
          </div>

          {/* Filters */}
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 mb-6'>
            {/* Search */}
            <div className='relative lg:col-span-2'>
              <Search
                className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground'
                size={16}
              />
              <input
                type='text'
                placeholder='Search by name, email or phone...'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className='w-full pl-10 pr-10 py-2 text-sm'
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className='absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground'
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => updateFilter('status', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value=''>All Status</option>
              {CUSTOMER_STATUSES.map((status) => (
                <option key={status.key} value={status.key}>
                  {status.label}
                </option>
              ))}
            </select>

            {/* KYC Status Filter */}
            {/* <select
              value={filters.kycStatus}
              onChange={(e) => updateFilter('kycStatus', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value=''>All KYC Status</option>
              {KYC_STATUSES.map((status) => (
                <option key={status.key} value={status.key}>
                  {status.label}
                </option>
              ))}
            </select> */}

            {/* Phone Verified Filter */}
            <select
              value={filters.phoneVerified}
              onChange={(e) => updateFilter('phoneVerified', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value=''>Phone Status</option>
              <option value='true'>Phone Verified</option>
              <option value='false'>Phone Not Verified</option>
            </select>

            {/* Items per page */}
            <select
              value={filters.limit}
              onChange={(e) => updateFilter('limit', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='5'>5 per page</option>
              <option value='10'>10 per page</option>
              <option value='20'>20 per page</option>
              <option value='50'>50 per page</option>
            </select>
          </div>

          {/* Table */}
          <div className='bg-card border border-border rounded-lg overflow-hidden'>
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead className='bg-muted border-b border-border'>
                  <tr>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Photo
                    </th>
                    <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground'
                      onClick={() => handleSort('firstName')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Name</span>
                        <SortIcon column='firstName' />
                      </div>
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Phone
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Email
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Phone Verified
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Email Verified
                    </th>
                    <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground'
                      onClick={() => handleSort('status')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Status</span>
                        <SortIcon column='status' />
                      </div>
                    </th>
                    {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      KYC Status
                    </th> */}
                    <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground'
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Created</span>
                        <SortIcon column='createdAt' />
                      </div>
                    </th>
                    <th className='px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-border'>
                  {isPending ? (
                    <tr>
                      <td
                        colSpan='10'
                        className='px-4 py-8 text-center text-muted-foreground'
                      >
                        Loading customers...
                      </td>
                    </tr>
                  ) : isError ? (
                    <tr>
                      <td
                        colSpan='10'
                        className='px-4 py-8 text-center text-destructive'
                      >
                        Error loading customers:{' '}
                        {error?.message || 'Something went wrong'}
                      </td>
                    </tr>
                  ) : customers?.length === 0 ? (
                    <tr>
                      <td
                        colSpan='10'
                        className='px-4 py-8 text-center text-muted-foreground'
                      >
                        No customers found
                      </td>
                    </tr>
                  ) : (
                    customers?.map((customer) => (
                      <tr
                        key={customer._id}
                        className='hover:bg-muted/50 transition-colors'
                      >
                        <td className='px-4 py-3'>
  {customer.profilePhoto ? (
    <img
      src={customer.profilePhoto}
      alt={`${customer.firstName || ''} ${customer.lastName || ''}`}
      className='w-10 h-10 rounded-full object-cover border border-border'
    />
  ) : (
    <div className='w-10 h-10 rounded-full bg-muted flex items-center justify-center border border-border'>
      <User className='w-5 h-5 text-muted-foreground' />
    </div>
  )}
</td>
                        <td className='px-4 py-3 text-sm font-medium text-foreground whitespace-nowrap'>
                          {customer.firstName || customer.lastName
                            ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim()
                            : 'N/A'}
                        </td>
                        <td className='px-4 py-3 text-sm text-foreground'>
                          {customer.countryCode}
                          {customer.phoneNumber}
                        </td>
                        <td className='px-4 py-3 text-sm text-foreground'>
                          {customer.email || 'N/A'}
                        </td>
                        <td className='px-4 py-3 text-sm'>
                          {getVerificationBadge(customer.phoneVerified)}
                        </td>
                        <td className='px-4 py-3 text-sm'>
                          {getVerificationBadge(customer.emailVerified)}
                        </td>
                        <td className='px-4 py-3 text-sm'>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full capitalize whitespace-nowrap ${getStatusColor(
                              customer.status,
                            )}`}
                          >
                            {customer.status?.replace('_', ' ') || 'N/A'}
                          </span>
                        </td>
                        {/* <td className='px-4 py-3 text-sm'>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full capitalize whitespace-nowrap ${getKycStatusColor(
                              customer.kyc?.status,
                            )}`}
                          >
                            {customer.kyc?.status?.replace('_', ' ') || 'N/A'}
                          </span>
                        </td> */}
                        <td className='px-4 py-3 text-sm text-foreground'>
                          {formatDateTime(customer.createdAt)}
                        </td>
                        <td className='px-4 py-3 text-sm text-right'>
                          <div className='flex items-center justify-end space-x-2'>
                            <button
                              className='inline-flex items-center px-3 py-1 text-sm bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-colors'
                              title='View Details'
                              onClick={() => handleViewClick(customer)}
                            >
                              <Eye
                                size={16}
                                className='mr-1'
                              />
                              View
                            </button>
                            {/* <button
                              className='p-1 hover:bg-muted rounded transition-colors'
                              title='Edit Customer'
                              onClick={() => handleEditClick(customer)}
                            >
                              <Edit
                                size={16}
                                className='text-muted-foreground hover:text-foreground'
                              />
                            </button> 
                            <button
                              onClick={() => handleDeleteClick(customer)}
                              className='p-1 hover:bg-muted rounded transition-colors'
                              title='Delete Customer'
                            >
                              <Trash2
                                size={16}
                                className='text-muted-foreground hover:text-destructive'
                              />
                            </button>  */}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && (
              <div className='flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-border space-y-3 sm:space-y-0'>
                <div className='text-sm text-muted-foreground'>
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.total,
                  )}{' '}
                  of {pagination.total} customers
                </div>

                <div className='flex items-center space-x-2'>
                  <button
                    onClick={() =>
                      updateFilter('page', String(pagination.page - 1))
                    }
                    disabled={pagination.page === 1}
                    className='p-2 border border-border rounded-md bg-card text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div className='flex items-center space-x-1'>
                    {[...Array(pagination.totalPages)].map((_, idx) => {
                      const page = idx + 1;
                      const current = pagination.page;

                      // Show first, last, current, and adjacent pages
                      if (
                        page === 1 ||
                        page === pagination.totalPages ||
                        (page >= current - 1 && page <= current + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => updateFilter('page', String(page))}
                            className={`min-w-[2rem] px-3 py-1 text-sm rounded-md transition-colors ${
                              page === current
                                ? 'bg-primary text-primary-foreground font-medium'
                                : 'bg-card text-muted-foreground hover:bg-accent hover:text-foreground border border-border'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === current - 2 || page === current + 2) {
                        return (
                          <span key={page} className='text-muted-foreground'>
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() =>
                      updateFilter('page', String(pagination.page + 1))
                    }
                    disabled={pagination.page === pagination.totalPages}
                    className='p-2 border border-border rounded-md bg-card text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* TODO: Add Modals */}
        {/* <AddCustomerModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} /> */}
        {/* <EditCustomerModal isEditModalOpen={isEditModalOpen} setIsEditModalOpen={setIsEditModalOpen} customer={selectedCustomer} /> */}
        {/* <DeleteConfirmModal isDeleteModalOpen={isDeleteModalOpen} setIsDeleteModalOpen={setIsDeleteModalOpen} customer={selectedCustomer} /> */}
      </div>
    </Layout>
  );
};

export default CustomerManagement;
